import { useState } from "react"
import { Link } from "@tanstack/react-router"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Label } from "@/components/ui/label"
import {
  Search,
  Plus,
  Edit,
  ChevronLeft,
  ChevronRight,
  Cpu,
  Loader2,
  AlertCircle,
} from "lucide-react"
import { useApiQuery } from "@/api/ApiQueryServiceProvider"
import type { GetDevicesRegistry } from "@generated-schemas"
import type { InferResponse } from "@/types/registryTypes"

// Derive the device shape and status type directly from the Zod schema
export type Device = InferResponse<typeof GetDevicesRegistry>["content"]["devices"][number]
const statusConfig: Record<
  string,
  {
    variant: "default" | "secondary" | "destructive" | "outline" | "warning" | "info" | "success"
    label: string
    className?: string
  }
> = {
  procured: { variant: "warning", label: "Procured" },
  provisioned: { variant: "success", label: "Active" },
  retired: { variant: "secondary", label: "Retired" },
  faulty: { variant: "destructive", label: "Faulty" },
  returned: { variant: "warning", label: "Returned" },
  ordered: { variant: "info", label: "Ordered" },
  shipped: { variant: "info", label: "Shipped" },
  received: { variant: "info", label: "Received" },
  testing: { variant: "warning", label: "Testing" },
  tested: { variant: "success", label: "Tested" },
  available: { variant: "success", label: "Available" },
  live: { variant: "success", label: "Live" },
}

const defaultStatusConfig = {
  variant: "default" as const,
  label: "Unknown",
}

interface DeviceListProps {
  onSelectDevice?: (device: Device) => void
  onAdd?: () => void
}

export function DeviceList({ onSelectDevice, onAdd }: DeviceListProps) {
  const [searchQuery, setSearchQuery] = useState("")
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState<number | "all">(10)

  // Fetch devices from API
  const {
    data: devicesResponse,
    isLoading,
    error,
    refetch,
  } = useApiQuery("getDevices")


  const devices = devicesResponse?.content?.devices || []

  const filteredDevices = devices.filter((device) => {
    if (!searchQuery) return true // Show all devices if search is empty
    const query = searchQuery.toLowerCase()
    return (
      device.deviceNumber.toString().includes(searchQuery) ||
      device.deviceName?.toLowerCase().includes(query) ||
      device.deviceTypeName?.toLowerCase().includes(query)
    )
  })

  const effectiveItemsPerPage = itemsPerPage === "all" ? filteredDevices.length : itemsPerPage
  const totalPages = itemsPerPage === "all" ? 1 : Math.ceil(filteredDevices.length / itemsPerPage)
  const startIndex = (currentPage - 1) * effectiveItemsPerPage
  const paginatedDevices = itemsPerPage === "all" 
    ? filteredDevices 
    : filteredDevices.slice(startIndex, startIndex + effectiveItemsPerPage)


  return (
    <div className="space-y-4">
      {/* Header Card */}
      <Card className="shadow-sm pb-0">
        <CardHeader className="border-b bg-slate-700 text-white [&_p]:text-slate-300">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                <Cpu className="h-5 w-5 text-primary" />
              </div>
              <div>
                <CardTitle className="text-xl">Devices</CardTitle>
                <CardDescription className="text-base">Manage and monitor IoT devices</CardDescription>
              </div>
            </div>

            <Button onClick={() => onAdd?.()}>
              <Plus className="h-4 w-4 mr-2" />
              Order Device
            </Button>
          </div>
        </CardHeader>
      </Card>

      {/* Search, Filters and Device Table */}
      <Card className="shadow-sm pb-0">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            <div className="relative w-full sm:max-w-sm">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search by device number, name, or type..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value)
                setCurrentPage(1)
              }}
                className="pl-9 h-10"
            />
          </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span className="font-medium text-foreground">{filteredDevices.length}</span>
              <span>device{filteredDevices.length !== 1 ? 's' : ''} found</span>
            </div>
        </div>
        </CardContent>
      </Card>

      {/* Device List Table */}
      <Card className="shadow-sm pb-0">
        <CardContent className="p-4">

        {/* Table */}
        <Table className="text-base">
          <TableHeader>
            <TableRow className="bg-muted/30 hover:bg-muted/30">
              <TableHead className="font-semibold text-base">Device</TableHead>
              <TableHead className="font-semibold text-base">Name</TableHead>
              <TableHead className="font-semibold text-base">Type</TableHead>
              <TableHead className="font-semibold text-base">Category</TableHead>
              <TableHead className="font-semibold text-base">Status</TableHead>
              <TableHead className="w-[120px] text-right font-semibold text-base">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={6} className="h-24 text-center">
                  <div className="flex items-center justify-center gap-2">
                    <Loader2 className="h-5 w-5 animate-spin text-primary" />
                    <span className="text-muted-foreground">Loading devices...</span>
                  </div>
                </TableCell>
              </TableRow>
            ) : error ? (
              <TableRow>
                <TableCell colSpan={6} className="h-24 text-center">
                  <div className="flex flex-col items-center gap-2 text-destructive">
                    <AlertCircle className="h-8 w-8" />
                    <p>Failed to load devices</p>
                    <p className="text-sm text-muted-foreground">{error.message}</p>
                    <Button variant="outline" size="sm" onClick={() => refetch()}>
                      Retry
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ) : paginatedDevices.length > 0 ? (
              paginatedDevices.map((device) => {
                const status = statusConfig[device.deviceStatus] || defaultStatusConfig
                return (
                  <TableRow
                    key={device.deviceNumber}
                    className="cursor-pointer transition-colors hover:bg-muted/50"
                    onClick={() => onSelectDevice?.(device)}
                  >
                    <TableCell className="text-base text-muted-foreground">
                      {device.deviceNumber}
                    </TableCell>
                    <TableCell className="font-medium text-base">
                      <span className="font-medium text-primary hover:underline">{device.deviceName || "—"}</span>
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">{device.deviceTypeName || "—"}</div>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-muted-foreground">
                      {device.deviceTypeCategory || "—"}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge variant={status.variant} className={status.className}>
                        {status.label}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button asChild variant="ghost" size="icon" className="h-8 w-8">
                          <Link
                            to="/sensory/devices/$deviceNumber/edit"
                            params={{ deviceNumber: String(device.deviceNumber) }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Edit className="h-4 w-4" />
                            <span className="sr-only">Edit device {device.deviceNumber}</span>
                          </Link>
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                )
              })
            ) : (
              <TableRow>
                <TableCell colSpan={6} className="h-24 text-center text-muted-foreground">
                  {searchQuery ? `No devices match "${searchQuery}".` : 'No devices found.'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        {/* Pagination */}
        {filteredDevices.length > 0 && (
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-t bg-muted/20 py-4">
            <div className="flex items-center gap-2 text-base">
              <span className="text-muted-foreground">Showing</span>
              <span className="font-semibold text-foreground">{startIndex + 1}</span>
              <span className="text-muted-foreground">to</span>
              <span className="font-semibold text-foreground">{Math.min(startIndex + effectiveItemsPerPage, filteredDevices.length)}</span>
              <span className="text-muted-foreground">of</span>
              <span className="font-semibold text-foreground">{filteredDevices.length}</span>
              <span className="text-muted-foreground">devices</span>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="flex items-center gap-2">
                <Label htmlFor="itemsPerPage" className="text-sm font-medium whitespace-nowrap">
                Items per page:
              </Label>
              <Select
                value={itemsPerPage.toString()}
                onValueChange={(value) => {
                  setItemsPerPage(value === "all" ? "all" : Number(value))
                  setCurrentPage(1)
                }}
              >
                  <SelectTrigger className="h-9 w-[110px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="20">20</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                  <SelectItem value="all">Show All</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {totalPages > 1 && (
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="icon"
                    className="h-9 w-9"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((p) => p - 1)}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                  <div className="flex items-center gap-1 px-3">
                <span className="text-base font-medium">
                  Page {currentPage} of {totalPages}
                </span>
                  </div>
                <Button
                  variant="outline"
                  size="icon"
                    className="h-9 w-9"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage((p) => p + 1)}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>

    </div>
  )
}
